/*
 * Un serviciu din catalog, în rezultatele căsuței.
 *
 * Scos din `SelfServeSearch` odată cu împărțirea ecranului în două coloane:
 * căsuța a rămas doar cu întrebarea, iar rezultatele se desenează în coloana
 * din dreapta, deci cardul trebuie să poată fi folosit din amândouă locurile.
 * Perechea lui `ArticleCard`, extras mai devreme din același motiv.
 *
 * Forma scurtă a cardului de catalog: aici omul se uită la un rând, nu la o
 * fișă. Cele două cifre de contract respectă aceeași vizibilitate ca în
 * catalog — când furnizorul le-a ascuns, cheia lipsește și nu se desenează.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import * as shop from '../api/shop';
import type { CatalogItem } from '../api/types';
import { useCounts } from './CountsContext';
import { useToasts } from './ToastContext';
import { pricingHeadline } from '../lib/format';
import { t } from '../lib/i18n';

export function ServiceRow({ item, searchId }: { item: CatalogItem; searchId: number }) {
  const { refreshCart } = useCounts();
  const { push } = useToasts();
  const [busy, setBusy] = useState(false);

  const add = async () => {
    setBusy(true);

    try {
      await shop.addToCart(item.id, 1, '');
      push(`${item.name} added to cart.`);
      refreshCart();

      /* Pe drum, si fara sa astepte dupa ea: e o masura pentru noi. Vezi
         ArticleCard pentru acelasi rationament pe deschiderea unui articol. */
      if (searchId > 0) {
        void shop.searchOutcome(searchId, 'cart').catch(() => {});
      }
    } catch (cause) {
      push(cause instanceof Error ? cause.message : t('Could not add to cart.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="card">
      <p className="card__eyebrow micro-label">{item.category?.name ?? t('Uncategorised')}</p>
      <h3 className="card__title">
        <Link to={`/catalog/${item.id}`}>{item.name}</Link>
      </h3>
      {item.short_description !== '' ? <p className="card__text">{item.short_description}</p> : null}

      {/*
        * Aceleași două cifre ca pe cardul din catalog, cu aceeași regulă: un
        * fapt pe care furnizorul l-a ascuns nu vine deloc, deci nu se
        * desenează. Nu „—", nu „No charge".
        *
        * AMÂNDOUĂ, nu doar costul. Prima variantă arăta aici numai prețul, pe
        * motiv că un rând de căutare e mai scurt decât un card — dar comutatorul
        * din portal spune „arată SLA-ul celui care cere", iar a-l ascunde aici
        * ar fi însemnat să hotărâm noi altceva decât furnizorul, pe un ecran în
        * care el nici nu știe că se afișează servicii.
        */}
      {item.pricing || item.sla !== undefined ? (
        <dl className="card__facts">
          {item.pricing ? (
            <div>
              <dt className="micro-label">{t('Service cost')}</dt>
              <dd className="num">{pricingHeadline(item.pricing)}</dd>
            </div>
          ) : null}
          {item.sla !== undefined ? (
            <div>
              <dt className="micro-label">{t('Response SLA')}</dt>
              <dd>{item.sla ?? '—'}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}

      <div className="card__actions">
        <Link className="btn btn--sm" to={`/catalog/${item.id}`}>{t('Details')}</Link>
        <button type="button" className="btn btn--sm btn--primary" disabled={busy} onClick={add}>
          {busy ? t('Adding…') : t('Add to cart')}
        </button>
      </div>
    </article>
  );
}
